import { type ReactNode, useEffect, useRef, useState } from 'react';
import { IconeCopiar } from './icones';

/**
 * `CodigoDestaque` — a placa (DESIGN-FOUNDATION §5.2 / §6.1). Bloco de largura
 * total, 96px, `--grafite`, `--r-3`, luz de 1px no topo, **sem sombra**. A placa
 * inteira é o alvo de toque; tocar copia o código e dispara `onCopiar`.
 * É a única coisa centralizada do portal, junto do wordmark.
 *
 * Animação 1/3 (§4.1): ao copiar, a placa clareia 6% e volta; o chip "Código
 * copiado" entra com fade e some em 1.6s.
 */
export function CodigoDestaque({
  codigo,
  aoCopiar,
}: {
  codigo: string;
  aoCopiar?: () => void;
}): ReactNode {
  const [copiado, setCopiado] = useState(false);
  const [pulso, setPulso] = useState(0);
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const copiar = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(codigo);
    } catch {
      /* clipboard indisponível — o código continua visível para ditar/anotar */
    }
    setCopiado(true);
    setPulso((n) => n + 1);
    aoCopiar?.();
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setCopiado(false), 1600);
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => void copiar()}
        aria-label={`Copiar o código ${codigo}`}
        className="relative flex h-24 w-full items-center justify-center rounded-3 bg-grafite text-placa-tinta shadow-[inset_0_1px_0_rgba(255,255,255,0.14)] dark:border dark:border-linha-forte"
      >
        <span key={pulso} className={pulso ? 'anim-placa-copiada block' : 'block'}>
          <span className="tipo-rotulo mb-2 block opacity-60">Seu código</span>
          <span className="tipo-placa block">{codigo}</span>
        </span>
        <IconeCopiar
          className="absolute right-5 top-1/2 -translate-y-1/2 opacity-60"
          width={18}
          height={18}
        />
      </button>

      <span
        aria-live="polite"
        className={
          'pointer-events-none absolute left-1/2 top-3 -translate-x-1/2 rounded-1 border border-acento-borda bg-acento-fraco px-2 py-1 tipo-rotulo-xs text-acento-texto transition-opacity ' +
          (copiado ? 'opacity-100' : 'opacity-0')
        }
      >
        {copiado ? 'Código copiado' : ''}
      </span>
    </div>
  );
}
